import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { inr } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Minus, Plus, ShoppingCart, Truck, ShieldCheck, RotateCcw, Clock, LogIn } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const { add } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setP(r.data));
  }, [id]);

  if (!p) return (<div className="min-h-screen bg-sand"><Navbar /><div className="max-w-7xl mx-auto px-6 py-20 text-navy/50">Loading…</div></div>);

  const isComingSoon = !!p.is_coming_soon;

  const handleAdd = () => {
    if (!user) {
      toast.info("Please log in to purchase");
      nav("/login", { state: { from: `/product/${id}` } });
      return;
    }
    if (isComingSoon) {
      toast.info(`${p.name} is Coming Soon`);
      return;
    }
    add(p, qty);
    toast.success("Added to cart");
  };

  const handleBuyNow = () => {
    if (!user) { toast.info("Please log in to purchase"); nav("/login", { state: { from: `/product/${id}` } }); return; }
    if (isComingSoon) { toast.info(`${p.name} is Coming Soon`); return; }
    add(p, qty);
    nav("/cart");
  };

  return (
    <div className="min-h-screen bg-sand">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/products" className="text-sm text-navy/60 hover:text-gold">← Back to shop</Link>
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="relative bg-white border border-border rounded-sm overflow-hidden aspect-square">
            <img
              src={p.image || "/product-images/coming-soon.svg"}
              alt={p.name}
              onError={(e) => { e.currentTarget.src = "/product-images/coming-soon.svg"; }}
              className={`w-full h-full object-cover ${isComingSoon ? "grayscale opacity-80" : ""}`}
            />
            {isComingSoon && (
              <span className="absolute inset-x-0 bottom-0 bg-royal/95 text-gold text-xs font-semibold uppercase tracking-widest px-3 py-2.5 inline-flex items-center justify-center gap-2">
                <Clock size={13} /> Coming Soon
              </span>
            )}
          </div>
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold">{(p.category || "").replace(/_/g, " ")}</div>
            <h1 className="font-serif text-4xl text-navy mt-3" data-testid="pd-title">{p.name}</h1>
            <div className="mt-2 text-sm text-navy/50">SKU {p.sku} · Brand {p.brand}</div>
            <div className="mt-6 flex items-end gap-4">
              <span data-testid="pd-price" className="text-4xl text-navy font-extrabold tabular-nums lining-nums">{inr(user ? p.dp : p.mrp)}</span>
              {p.mrp > p.dp && <span className="text-navy/40 line-through mb-1 tabular-nums lining-nums">{inr(p.mrp)}</span>}
              {!isComingSoon && <span className="ml-auto bg-navy text-gold px-3 py-1 text-xs font-bold">{p.sp} SP</span>}
            </div>
            <p className="mt-6 text-navy/70 leading-relaxed">{p.description}</p>
            {p.benefits && <div className="mt-4 text-sm text-navy/80"><strong>Benefits:</strong> {p.benefits}</div>}

            {isComingSoon ? (
              <div className="mt-8 p-5 bg-royal/5 border border-royal/20 rounded-sm">
                <div className="flex items-center gap-2 text-royal">
                  <Clock size={16} />
                  <span className="text-sm font-medium uppercase tracking-widest">Not Yet Available</span>
                </div>
                <p className="mt-2 text-sm text-navy/70">This product is Coming Soon. Log in to your IBA account and use the Notify Me option on the storefront to get an email when it goes live.</p>
              </div>
            ) : (
              <div className="mt-8 flex items-center gap-4">
                <div className="flex items-center border border-border rounded-sm">
                  <button data-testid="pd-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 hover:bg-cream"><Minus size={14} /></button>
                  <span className="px-4 text-sm font-medium" data-testid="pd-qty">{qty}</span>
                  <button data-testid="pd-qty-plus" onClick={() => setQty(qty + 1)} className="px-3 py-2 hover:bg-cream"><Plus size={14} /></button>
                </div>
                <button
                  data-testid="pd-add-cart"
                  onClick={handleAdd}
                  className="flex-1 bg-navy text-white py-3 hover:bg-navy-deep transition-colors inline-flex items-center justify-center gap-2 rounded-sm"
                >
                  {user ? <><ShoppingCart size={16} /> Add to Cart</> : <><LogIn size={16} /> Login to Purchase</>}
                </button>
                <button
                  onClick={handleBuyNow}
                  data-testid="pd-buy-now"
                  className="px-6 py-3 bg-gold text-navy font-medium hover:bg-gold-deep transition-colors rounded-sm"
                >
                  {user ? "Buy now" : "Login"}
                </button>
              </div>
            )}

            {!user && !isComingSoon && (
              <p className="mt-3 text-xs text-navy/50">
                Purchases require an account. <Link to="/register" className="text-royal hover:text-gold underline">Register</Link> or <Link to="/login" className="text-royal hover:text-gold underline">Login</Link> to continue.
              </p>
            )}

            <div className="mt-10 grid grid-cols-3 gap-4 border-t border-border pt-6">
              {[{ i: Truck, l: "Free shipping ₹999+" }, { i: ShieldCheck, l: "GST Invoice" }, { i: RotateCcw, l: "7-day returns" }].map((x, i) => (
                <div key={x.l} className="flex flex-col items-center text-center gap-2 text-xs text-navy/60">
                  <x.i size={20} className="text-gold" /> {x.l}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
